import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { Box, Button, Chip, MenuItem, SelectField, Stack, TextField, Tooltip } from '@curves/ui-base';
import { Overview } from '@curves/ui-shared';
import { useLocation } from 'react-router-dom';

import { ClubDialogBox } from '@Local/ClubCompany/Dialog';
import { PageLayout } from '@Local/Components/Layout';
import useClubApi from '@Apis/useClubApi';
import useOverview from '@Util/hook/useOverview';
import { CLUB_STATUS_ALL, CLUB_STATUS_TEXT_MAP } from '@Local/ClubCompany/staticData';

const defaultSearchParams = {
    clubStatus: CLUB_STATUS_ALL,
    pageIndex: 1,
    pageSize: 20,
};

const AccountChip = ({ label, isExist }) => (
    <Box style={{ opacity: isExist ? 1 : 0.45 }}>
        <Chip className={'cursor-pointer'} label={label} size="small" variant={'filled'} color={isExist ? 'primary' : 'default'} />
    </Box>
);

const ClubOverviewPage = () => {
    const { postClubsOverviewApi, getClubApi, getClubsOptionsApi } = useClubApi();
    const location = useLocation();
    const dialogRef = useRef(null);
    const clubCodeRef = useRef(null);
    const clubNameRef = useRef(null);
    const companyNameRef = useRef(null);
    const clubStatusRef = useRef(null);
    const [clubStatusOptions, setClubStatusOptions] = useState([]);
    const { overviewData, getOverviewData, searchParams } = useOverview({
        getOverviewApi: postClubsOverviewApi,
        defaultSearchParams,
        isFetchOnMount: true,
        enableQueryString: true,
    });

    // 搜尋列的狀態下拉需要「全部」選項，故 skipAll 帶 false
    useEffect(() => {
        (async () => {
            const resp = await getClubsOptionsApi(false);
            setClubStatusOptions(resp?.clubStatusOptions ?? []);
        })();
        // eslint-disable-next-line
    }, []);

    // Dialog 非同步完成後仍需沿用使用者最後一次的搜尋條件，因此以 ref 避免 callback 捕捉到舊 state。
    const searchParamsRef = useRef(searchParams);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const handleGetOverviewData = params => {
        // 先同步 ref，讓新增／編輯／補建帳號成功後的 refresh 可立即用同一組條件重查。
        searchParamsRef.current = params;
        return getOverviewData(params);
    };

    // 新增成功需回第 1 頁才看得到新店(伺服器依 ClubID 遞減排序)；編輯與補建帳號則保留當前頁碼
    const refreshClubs = (isResetPage = false) =>
        handleGetOverviewData(isResetPage ? { ...searchParamsRef.current, pageIndex: 1 } : searchParamsRef.current);

    const handlePageChange = (e, page) => {
        // 換頁只更新頁碼，保留目前所有搜尋條件。
        handleGetOverviewData({ ...searchParamsRef.current, pageIndex: page });
    };

    const getSearchParams = () => {
        const clubCode = (clubCodeRef.current?.getResult?.() ?? '').trim();
        const clubName = (clubNameRef.current?.getResult?.() ?? '').trim();
        const companyName = (companyNameRef.current?.getResult?.() ?? '').trim();
        const clubStatus = Number(clubStatusRef.current?.getResult?.() ?? 0) || 0;

        return {
            clubCode,
            clubName,
            companyName,
            clubStatus,
            pageIndex: 1,
            pageSize: 20,
        };
    };

    const contextValue = {
        overviewData,
        searchParams,
        getOverviewData: handleGetOverviewData,
        updateOverviewPage: page => handlePageChange(null, page),
    };

    // 由公司管理頁(②)導入，帶著新建立的公司代碼，自動開啟新增店舖 Dialog 並預選公司
    useEffect(() => {
        const preselectCompany = location.state?.preselectCompany;
        if (preselectCompany) {
            handleOpenClubDialog(preselectCompany);
        }
        // eslint-disable-next-line
    }, [location.state]);

    const handleOpenClubDialog = async (targetData = null) => {
        let sourceData = targetData;

        if (targetData?.clubID) {
            sourceData = await getClubApi(targetData.clubID);
        }

        if (dialogRef.current && dialogRef.current.openClubDialog) {
            const isEdit = !!targetData?.clubID;
            dialogRef.current.openClubDialog(sourceData, () => refreshClubs(!isEdit));
        }
    };

    const handleOpenEnsureAccountsDialog = (targetData) => {
        if (dialogRef.current && dialogRef.current.openEnsureAccountsDialog) {
            dialogRef.current.openEnsureAccountsDialog(targetData, refreshClubs);
        }
    };

    const pageActions = [
        {
            label: '新增店舖',
            variant: 'contained',
            onClick: () => handleOpenClubDialog(null),
        },
    ];

    const headerRow = [
            { cellKey: 'clubCode', headerLabel: '店舖代碼' },
            { cellKey: 'clubName', headerLabel: '店舖名稱' },
            { cellKey: 'companyName', headerLabel: '公司名稱' },
            {
                cellKey: 'clubStatus',
                headerLabel: '狀態',
                formatCell: clubStatus => CLUB_STATUS_TEXT_MAP[clubStatus] ?? CLUB_STATUS_TEXT_MAP[0],
            },
            {
                cellKey: 'hasCommonAccount',
                headerLabel: '帳號',
                formatCell: (hasCommonAccount, rowData) => (
                    <Tooltip title={'檢查／補建帳號'}>
                        <Box className={'cursor-pointer'} onClick={() => handleOpenEnsureAccountsDialog(rowData)}>
                            <Stack direction={'row'} spacing={1}>
                                <AccountChip label={'通用'} isExist={hasCommonAccount} />
                                <AccountChip label={'加盟主'} isExist={rowData.hasOwnerAccount} />
                                <AccountChip label={'管理者'} isExist={rowData.hasAdminAccount} />
                            </Stack>
                        </Box>
                    </Tooltip>
                ),
            },
            {
                cellKey: 'action',
                headerLabel: '操作',
                align: 'center',
                formatCell: (_, rowData) => (
                    <Stack direction={'row'} spacing={1} justifyContent={'center'}>
                        <Button variant={'outlined'} size={'small'} color="primary" onClick={() => handleOpenClubDialog(rowData)}>
                            編輯
                        </Button>
                    </Stack>
                ),
            },
    ];

    return (
        <PageLayout className={'club-overview-container'}>
            <PageLayout.Header title="店舖總覽" actions={pageActions} />
            <Overview contextValue={contextValue} idKey="clubID">
                <Overview.Search className="p-3 mb-1" getSearchParamsOnSubmit={getSearchParams}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.clubCode || ''}
                                ref={clubCodeRef}
                                label={'店舖代碼'}
                                defaultValue={searchParams?.clubCode || ''}
                                InputProps={{ placeholder: '請輸入店舖代碼' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.clubName || ''}
                                ref={clubNameRef}
                                label={'店舖名稱'}
                                defaultValue={searchParams?.clubName || ''}
                                InputProps={{ placeholder: '請輸入店舖名稱' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                key={searchParams?.companyName || ''}
                                ref={companyNameRef}
                                label={'公司名稱'}
                                defaultValue={searchParams?.companyName || ''}
                                InputProps={{ placeholder: '請輸入公司名稱' }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <SelectField
                                key={`${searchParams?.clubStatus ?? 0}-${clubStatusOptions.length}`}
                                ref={clubStatusRef}
                                label={'店舖狀態'}
                                defaultValue={String(searchParams?.clubStatus || CLUB_STATUS_ALL)}
                                fullWidth
                            >
                                {clubStatusOptions.map(({ text, value }) => (
                                    <MenuItem key={value} value={value}>
                                        {text}
                                    </MenuItem>
                                ))}
                            </SelectField>
                        </Grid>
                    </Grid>
                    <Stack className={'mt-2'} justifyContent={'end'} spacing={2}>
                        <Button variant={'contained'} color="primary" type="submit">
                            搜尋
                        </Button>
                    </Stack>
                </Overview.Search>
                <Stack className={'overview__toolbar'} alignItems={'center'} justifyContent={'end'}>
                    <Overview.TotalCount showRange />
                </Stack>
                <Overview.Table headerRow={headerRow} />
                <Overview.Pagination className="mt-4" />
            </Overview>
            <ClubDialogBox ref={dialogRef} />
        </PageLayout>
    );
};

export default ClubOverviewPage;
